import { MCP_CATALOG_FORM_DEFAULT_VALUES, MCP_CATALOG_MODE } from '../McpCatalogConstant';
import { parseSupportedOS } from '@/common/utils/mcpUtils';
import trI18n from '@/common/utils/trI18n';

/**
 * Build the display text of a backend field error from its message code.
 * Called again whenever the translation dictionary changes, so the text never stays stale.
 * @param {string} messageCode - I18n key returned by the backend
 * @param {object|null} params - values for {{key}} placeholders in the message
 * @returns {string} translated message
 */
export const buildServerErrorMessage = (messageCode, params) => {
    let message = trI18n(messageCode) || messageCode;
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            message = message.replace(`{{${key}}}`, String(value));
        });
    }
    return message;
};

/**
 * Build the react-hook-form error object for a backend field error.
 * Keeps messageCode/params so the message can be translated again later.
 * @param {string} messageCode - I18n key returned by the backend
 * @param {object|null} params - values for {{key}} placeholders in the message
 * @returns {object} error object for setError
 */
export const buildServerFieldError = (messageCode, params) => ({
    type: 'server',
    message: buildServerErrorMessage(messageCode, params),
    messageCode,
    params: params || null
});

/**
 * Build form default values from item data (edit mode) or empty (create mode)
 * @param {object|null} item - MCP catalog item data
 * @returns {object} form default values
 */
export const buildFormDefaultValues = item => {
    if (!item) {
        return { ...MCP_CATALOG_FORM_DEFAULT_VALUES };
    }

    return {
        mcpName: item.mcpName || '',
        mcpPackageName: item.mcpPackageName || '',
        mcpPackageVersion: item.mcpPackageVersion || '',
        mcpType: item.mcpType || '',
        description: item.description || '',
        purpose: item.purpose || '',
        supportedOS: parseSupportedOS(item.supportedOS),
        downloadURL: item.downloadURL || '',
        prerequisites: item.prerequisites || '',
        requiredEnv: item.requiredEnv || '',
        systemPrompt: item.systemPrompt || ''
    };
};

/**
 * Format form data for submission
 * Note: `version` is system-managed on the backend (BR-5/BR-6), not sent from the form.
 * @param {object} formData - form values from react-hook-form
 * @param {string} mode - MCP_CATALOG_MODE.CREATE or MCP_CATALOG_MODE.EDIT
 * @param {object|null} originalItem - original item data (edit mode)
 * @returns {object} formatted data for API
 */
export const formatFormDataForSubmit = (formData, mode, originalItem) => {
    const payload = {
        mcpName: formData.mcpName?.trim() || '',
        mcpPackageName: formData.mcpPackageName?.trim() || '',
        mcpPackageVersion: formData.mcpPackageVersion?.trim() || '',
        mcpType: formData.mcpType || '',
        description: formData.description?.trim() || '',
        purpose: formData.purpose?.trim() || '',
        supportedOS: parseSupportedOS(formData.supportedOS),
        downloadURL: formData.downloadURL?.trim() || '',
        prerequisites: formData.prerequisites?.trim() || '',
        requiredEnv: formData.requiredEnv?.trim() || '',
        systemPrompt: formData.systemPrompt || ''
    };

    if (mode === MCP_CATALOG_MODE.EDIT && originalItem) {
        payload.id = originalItem.id;
    }

    return payload;
};
